/**
 * IntegrationsHubPage — ממשקים וחיבורים
 *
 * Phase 28 + Phase 29: Generic, scalable Integrations Hub.
 * Phase 48: Shopify OAuth (Public App) — replaces manual api_token entry.
 *
 * Route: /app/settings/integrations
 *
 * Phase 28 – Provider grid with Connect / Edit modals.
 * Phase 29 – Shopify "ניהול ממשק" modal with:
 *   • "טען מוצרים משופיפיי" – calls Edge Function shopify-fetch-products
 *   • Product selection table (image, title, SKU, price, checkbox)
 *   • "ייבא מוצרים נבחרים" – upserts into inventory_products via Supabase JS
 * Phase 48 – Shopify OAuth:
 *   • "חבר עכשיו" opens a popup to Shopify's authorization URL.
 *   • After consent, Shopify redirects to the `shopify-auth` Edge Function.
 *   • The Edge Function exchanges the code, stores the token in
 *     shopify_oauth_tokens (per-user), and posts SHOPIFY_AUTH_SUCCESS.
 *   • The popup message listener refreshes the integrations list.
 *
 * Architecture (security):
 *   The Shopify access token is NEVER sent to the browser.
 *   All server-to-Shopify calls go through Supabase Edge Functions
 *   which read credentials from shopify_oauth_tokens using the service
 *   role key, and validate the caller's JWT before making Shopify requests.
 *
 * Data:
 *   company_integrations  → { store_url, oauth_user } (no api_token)
 *   shopify_oauth_tokens  → per-user OAuth token (Phase 48 migration)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, X, Plug, Settings, Unplug, ExternalLink, Clock, RefreshCw, Download, Package, ChevronDown, ChevronUp, Search, LayoutGrid, Wrench, SlidersHorizontal } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { emitToast } from '@/components/ui/Toast';
import { ConnectionIdentityCard } from '@/components/integrations/ConnectionIdentityCard';
import { integrationIdentityService } from '@/lib/integrationIdentityService';
import { SettingsTabs } from '@/components/settings';

// ─── Types ───────────────────────────────────────────────────────────────────

async function verifyAfterSave(integrationId: string) {
  try {
    const res = await integrationIdentityService.runCheck(integrationId);
    if (res.status === 'ok') {
      const who = res.identity?.account_name ?? res.identity?.account_ref;
      emitToast(who ? `החיבור תקין — מחובר ל: ${who}` : 'החיבור תקין', 'success');
    } else {
      emitToast(`הפרטים נשמרו, אך החיבור נכשל: ${res.error_message ?? 'שגיאה לא ידועה'}`, 'error');
    }
  } catch (e) {
    emitToast(e instanceof Error ? e.message : 'בדיקת החיבור נכשלה', 'error');
  }
}

interface Integration {
  id:            string;
  company_id:    string;
  provider_name: string;
  credentials:   Record<string, string>;
  is_active:     boolean;
  last_sync_at:  string | null;
  created_at:    string;
  updated_at:    string;
}

type ProviderStatus = 'active' | 'available' | 'coming_soon';

interface ProviderDef {
  id:          string;
  label:       string;
  description: string;
  logoText:    string;
  logoBg:      string;
  logoColor:   string;
  status:      ProviderStatus;
  docsUrl?:    string;
  fields: Array<{
    key:         string;
    label:       string;
    placeholder: string;
    type:        'text' | 'password';
    hint?:       string;
  }>;
}

// Normalised Shopify product returned by the Edge Function
interface ShopifyProduct {
  shopify_id:   number;
  title:        string;
  vendor:       string;
  product_type: string;
  status:       string;
  sku:          string;
  price:        number;
  cost:         number | null;
  image_url:    string | null;
  variants: Array<{
    shopify_variant_id: number;
    title:              string;
    sku:                string;
    price:              number;
    inventory_quantity: number;
  }>;
}

// ─── Provider catalogue ───────────────────────────────────────────────────────

const PROVIDERS: ProviderDef[] = [
  {
    id:          'shopify',
    label:       'Shopify',
    description: 'סנכרן מוצרים, הזמנות ומלאי עם חנות Shopify.',
    logoText:    '🛍️',
    logoBg:      'bg-green-50',
    logoColor:   'text-green-700',
    // Phase 53: OAuth flow paused — card disabled; use shopify_manual instead.
    status:      'coming_soon',
    docsUrl:     'https://shopify.dev/docs/apps/auth/oauth',
    fields: [],
  },
  {
    id:          'shopify_manual',
    label:       'Shopify (חיבור ידני)',
    description: 'חיבור ישיר עם Admin API Token — Custom App ב-Shopify.',
    logoText:    '🛍️',
    logoBg:      'bg-green-50',
    logoColor:   'text-green-700',
    status:      'available',
    docsUrl:     'https://shopify.dev/docs/apps/build/custom-apps',
    fields: [
      {
        key:         'store_domain',
        label:       'כתובת החנות (Store Domain)',
        placeholder: 'your-store.myshopify.com',
        type:        'text' as const,
        hint:        'הכנס רק את הדומיין ללא https://',
      },
      {
        key:         'client_id',
        label:       'Client ID',
        placeholder: 'abc123def456...',
        type:        'text' as const,
        hint:        'Shopify Admin → Apps → Develop apps → [Your app] → API credentials → API key',
      },
      {
        key:         'client_secret',
        label:       'Client Secret',
        placeholder: 'shpss_...',
        type:        'password' as const,
        hint:        'Shopify Admin → Apps → Develop apps → [Your app] → API credentials → API secret key',
      },
    ],
  },
  {
    id:          'rivhit',
    label:       'רווחית (Rivhit)',
    description: 'חיבור למערכת הנהלת חשבונות ישראלית – הפקת חשבוניות מתעודות משלוח.',
    logoText:    '🧾',
    logoBg:      'bg-sky-50',
    logoColor:   'text-sky-700',
    status:      'available',
    docsUrl:     'https://api.rivhit.co.il/online/RivhitOnlineAPI.svc/help',
    fields: [
      {
        key:         'api_token',
        label:       'Rivhit API Token',
        placeholder: 'הדבק כאן את ה-API Token שקיבלת מרווחית',
        type:        'password',
        hint:        'ניתן לקבל ב-רווחית → הגדרות → API → יצירת מפתח',
      },
    ],
  },
  {
    id:          'tranzila',
    label:       'טרנזילה (Tranzila)',
    description: 'הפקת חשבוניות בשירות החשבוניות של טרנזילה ישירות מתעודות משלוח.',
    logoText:    '💳',
    logoBg:      'bg-teal-50',
    logoColor:   'text-teal-700',
    status:      'available',
    docsUrl:     'https://docs.tranzila.com/docs/invoices/invoices-api',
    fields: [
      {
        key:         'terminal_name',
        label:       'שם המסוף (Terminal)',
        placeholder: 'my_terminal',
        type:        'text',
        hint:        'שם המסוף שלכם בטרנזילה',
      },
      {
        key:         'app_key',
        label:       'מפתח אפליקציה (App Key)',
        placeholder: 'הדביקו כאן את מפתח האפליקציה',
        type:        'password',
        hint:        'מתקבל מטרנזילה יחד עם שירות החשבוניות',
      },
      {
        key:         'secret_key',
        label:       'מפתח סודי (Secret)',
        placeholder: 'הדביקו כאן את המפתח הסודי',
        type:        'password',
      },
    ],
  },
  {
    id:          'financy',
    label:       'Financy (Open Finance)',
    description: 'משיכת תנועות בנק וכרטיסי אשראי אוטומטית לטבלת הבנק בתזרים.',
    logoText:    '🏦',
    logoBg:      'bg-teal-50',
    logoColor:   'text-teal-700',
    status:      'available',
    docsUrl:     'https://financy.open-finance.ai/settings',
    fields: [
      {
        key:         'client_id',
        label:       'Client ID',
        placeholder: 'הדבק כאן את ה-Client ID מ-Financy',
        type:        'text' as const,
        hint:        'Financy → Settings → API. מזהה ציבורי של האפליקציה שלך.',
      },
      {
        key:         'client_secret',
        label:       'Client Secret',
        placeholder: 'הדבק כאן את ה-Client Secret מ-Financy',
        type:        'password' as const,
        hint:        'Financy → Settings → API. נשמר בצורה מאובטחת ומשמש רק בצד השרת.',
      },
      {
        key:         'user_id',
        label:       'מזהה משתמש (User ID)',
        placeholder: 'המזהה שאיתו חיברת את חשבון הבנק ב-Financy',
        type:        'text' as const,
        hint:        'המזהה שהזנת כשחיברת את הבנק דרך ממשק Financy (אימייל, טלפון, או מזהה שבחרת).',
      },
    ],
  },
  {
    id:          'woocommerce',
    label:       'WooCommerce',
    description: 'חיבור לחנות WordPress/WooCommerce — בקרוב.',
    logoText:    '🛒',
    logoBg:      'bg-purple-50',
    logoColor:   'text-purple-700',
    status:      'coming_soon',
    fields:      [],
  },
  {
    id:          'magento',
    label:       'Magento / Adobe Commerce',
    description: 'אינטגרציה עם פלטפורמת Magento — בקרוב.',
    logoText:    '🔶',
    logoBg:      'bg-orange-50',
    logoColor:   'text-orange-700',
    status:      'coming_soon',
    fields:      [],
  },
  {
    id:          'priority_erp',
    label:       'Priority ERP',
    description: 'ייבוא/ייצוא נתונים מ-Priority — בקרוב.',
    logoText:    '🏢',
    logoBg:      'bg-blue-50',
    logoColor:   'text-blue-700',
    status:      'coming_soon',
    fields:      [],
  },
  {
    id:          'hubspot',
    label:       'HubSpot CRM',
    description: 'סנכרן לקוחות ועסקאות עם HubSpot — בקרוב.',
    logoText:    '🧡',
    logoBg:      'bg-amber-50',
    logoColor:   'text-amber-700',
    status:      'coming_soon',
    fields:      [],
  },
  {
    id:          'google_sheets',
    label:       'Google Sheets',
    description: 'ייצוא דוחות ישירות ל-Google Sheets — בקרוב.',
    logoText:    '📊',
    logoBg:      'bg-emerald-50',
    logoColor:   'text-emerald-700',
    status:      'coming_soon',
    fields:      [],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('he-IL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtPrice = (n: number) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 }).format(n);

// ─── ShopifyOAuthModal ───────────────────────────────────────────────────────
// Phase 48: Replaces ConfigModal for the Shopify provider.
// Opens Shopify's OAuth authorization URL in a popup window.
// The shopify-auth Edge Function handles the callback and stores the token.

function ShopifyOAuthModal({
  existing,
  onConnected,
  onClose,
}: {
  existing:    Integration | null;
  onConnected: () => void;
  onClose:     () => void;
}) {
  const [shopDomain, setShopDomain] = useState(
    existing?.credentials?.store_url ?? '',
  );
  const [connecting,   setConnecting]   = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  // OAuth redirect URI — points to our shopify-auth Edge Function
  const redirectUri = `${supabaseUrl}/functions/v1/shopify-auth`;

  const SHOPIFY_CLIENT_ID = import.meta.env.VITE_SHOPIFY_CLIENT_ID ?? '';

  // Required scopes for product sync + order reading
  const SCOPES = [
    'read_products',
    'write_products',
    'read_orders',
    'read_inventory',
  ].join(',');

  const handleConnect = async () => {
    const domain = shopDomain.trim()
      .replace(/^https?:\/\//i, '')
      .replace(/\/$/, '')
      .toLowerCase();

    if (!domain) {
      setError('אנא הכנס את כתובת החנות (לדוגמה: mystore.myshopify.com)');
      return;
    }
    if (!/^[a-z0-9][a-z0-9\-]*\.myshopify\.com$/.test(domain)) {
      setError('כתובת החנות חייבת להיות בפורמט: mystore.myshopify.com');
      return;
    }
    if (!SHOPIFY_CLIENT_ID) {
      setError('VITE_SHOPIFY_CLIENT_ID לא מוגדר. אנא בדוק את קובץ .env');
      return;
    }

    setError(null);
    setConnecting(true);

    // Get current user ID to pass as `state`
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('לא מחובר — אנא התחבר מחדש');
      setConnecting(false);
      return;
    }

    const authUrl = [
      `https://${domain}/admin/oauth/authorize`,
      `?client_id=${encodeURIComponent(SHOPIFY_CLIENT_ID)}`,
      `&scope=${encodeURIComponent(SCOPES)}`,
      `&redirect_uri=${encodeURIComponent(redirectUri)}`,
      `&state=${encodeURIComponent(user.id)}`,
    ].join('');

    // Open Shopify authorization in a centered popup
    const w = 600, h = 700;
    const left = Math.max(0, (window.screen.width  - w) / 2);
    const top  = Math.max(0, (window.screen.height - h) / 2);
    const popup = window.open(
      authUrl,
      'shopify_oauth',
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!popup) {
      setError('הדפדפן חסם את חלון הקופץ. אנא אפשר חלונות קופצים לאתר זה ונסה שוב.');
      setConnecting(false);
      return;
    }

    // Listen for the success postMessage from the shopify-auth Edge Function
    const onMessage = (ev: MessageEvent) => {
      if (ev.data?.type !== 'SHOPIFY_AUTH_SUCCESS') return;
      window.removeEventListener('message', onMessage);
      setConnecting(false);
      onConnected();
    };
    window.addEventListener('message', onMessage);

    // Fallback: poll popup closed state (in case postMessage origin differs)
    const pollId = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollId);
        window.removeEventListener('message', onMessage);
        setConnecting(false);
        // Refresh regardless — token may have been stored
        onConnected();
      }
    }, 800);
  };

  const handleDisconnect = async () => {
    if (!existing) return;
    if (!confirm('האם לנתק את האינטגרציה עם Shopify?')) return;
    setDisconnecting(true); setError(null);
    try {
      const { error: e } = await supabase
        .from('company_integrations')
        .delete()
        .eq('id', existing.id);
      if (e) throw e;
      onConnected(); // re-fetch
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בניתוק');
    } finally { setDisconnecting(false); }
  };

  const isConnected = !!existing?.is_active;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-xl">🛍️</div>
            <div>
              <h2 className="text-base font-bold text-gray-900">חיבור Shopify — OAuth</h2>
              <p className="text-xs text-gray-400">חיבור מאובטח דרך Shopify Public App</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}

          {isConnected && (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>מחובר לחנות <strong>{existing?.credentials?.store_url}</strong></span>
            </div>
          )}

          {isConnected && existing && (
            <ConnectionIdentityCard companyId={existing.company_id} integrationId={existing.id} providerName="shopify" />
          )}

          {!isConnected && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  כתובת החנות <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={shopDomain}
                  onChange={e => setShopDomain(e.target.value)}
                  placeholder="mystore.myshopify.com"
                  disabled={connecting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-green-400
                             disabled:bg-gray-50 disabled:text-gray-400"
                  dir="ltr"
                />
                <p className="text-xs text-gray-400">הכנס רק את הדומיין, ללא https://</p>
              </div>

              <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                <span className="shrink-0 mt-0.5">ℹ️</span>
                לחיצה על "חבר עכשיו" תפתח חלון Shopify שבו תאשר את הגישה.
                לאחר האישור, החלון ייסגר אוטומטית והחיבור יתבצע.
              </div>
            </>
          )}

          {isConnected && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              <span className="shrink-0 mt-0.5">🔄</span>
              כדי לחבר חנות אחרת, נתק תחילה את החיבור הנוכחי ואז חבר מחדש.
            </div>
          )}

          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
            <span className="shrink-0 mt-0.5">🔒</span>
            הגישה מאובטחת — הטוקן מאוחסן בשרת בלבד ולעולם אינו נחשף לדפדפן.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div>
            {isConnected && (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Unplug className="w-4 h-4" />
                {disconnecting ? 'מנתק…' : 'נתק חיבור'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={connecting}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              ביטול
            </button>
            {!isConnected && (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700
                           text-white text-sm font-semibold rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {connecting
                  ? <><Loader2 className="w-4 h-4 animate-spin" />מחבר…</>
                  : <><ExternalLink className="w-4 h-4" />חבר עכשיו</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ConfigModal ──────────────────────────────────────────────────────────────

function ConfigModal({
  provider,
  existing,
  companyId,
  onSaved,
  onClose,
}: {
  provider:  ProviderDef;
  existing:  Integration | null;
  companyId: string;
  onSaved:   () => void;
  onClose:   () => void;
}) {
  // ── Credential fields ──────────────────────────────────────────────────────
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of provider.fields) init[f.key] = existing?.credentials[f.key] ?? '';
    return init;
  });

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const handleSave = async () => {
    for (const f of provider.fields) {
      if (!fields[f.key]?.trim()) { setError(`השדה "${f.label}" הוא חובה`); return; }
    }
    setSaving(true); setError(null);
    try {
      const credentials: Record<string, string> = {};
      for (const f of provider.fields) credentials[f.key] = fields[f.key]!.trim();

      const { data: saved, error: e } = await supabase
        .from('company_integrations')
        .upsert(
          { company_id: companyId, provider_name: provider.id, credentials, is_active: true, updated_at: new Date().toISOString() },
          { onConflict: 'company_id,provider_name' },
        )
        .select('id')
        .maybeSingle();
      if (e) throw e;
      if (saved?.id) await verifyAfterSave(saved.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירת הגדרות');
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!existing || !confirm(`האם לנתק את האינטגרציה עם ${provider.label}?`)) return;
    setSaving(true); setError(null);
    try {
      const { error: e } = await supabase.from('company_integrations').delete().eq('id', existing.id);
      if (e) throw e;
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בניתוק');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${provider.logoBg}`}>
              {provider.logoText}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {existing ? `עריכת חיבור – ${provider.label}` : `חיבור ${provider.label}`}
              </h2>
              <p className="text-xs text-gray-400">{provider.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}
          {provider.docsUrl && (
            <a href={provider.docsUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
              <ExternalLink className="w-3.5 h-3.5" />מסמכי API של {provider.label}
            </a>
          )}
          {provider.fields.map(f => (
            <div key={f.key} className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">{f.label} <span className="text-red-500">*</span></label>
              <input
                type={f.type}
                value={fields[f.key] ?? ''}
                onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                disabled={saving}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-400
                           disabled:bg-gray-50 disabled:text-gray-400"
                dir="ltr"
              />
              {f.hint && <p className="text-xs text-gray-400">{f.hint}</p>}
            </div>
          ))}

          {existing && (
            <ConnectionIdentityCard companyId={companyId} integrationId={existing.id} providerName={provider.id} />
          )}

          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
            <span className="shrink-0 mt-0.5">🔒</span>
            פרטי הגישה מאוחסנים בצורה מאובטחת. פרטי החשבון המחובר מוצגים לבעל החברה בלבד.
          </div>
          {existing?.last_sync_at && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />סנכרון אחרון: {fmtDateTime(existing.last_sync_at)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div>
            {existing && (
              <button onClick={handleDisconnect} disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
                <Unplug className="w-4 h-4" />נתק חיבור
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
              ביטול
            </button>
            <button onClick={handleSave} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                               text-white text-sm font-semibold rounded-lg transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed shadow-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
              {saving ? 'שומר…' : existing ? 'עדכן חיבור' : 'חבר עכשיו'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ShopifyManualModal ───────────────────────────────────────────────────────
// Supports two authentication modes selectable by the user:
//
//  Mode A — "Admin API Token" (api_token / shpat_…):
//    The user generates a permanent token inside their Shopify Admin panel.
//    Works for ALL store types including live/paid stores.
//    Path: Shopify Admin → Settings → Apps → Develop apps → [App] → API credentials
//
//  Mode B — "Client ID + Secret" (client_credentials grant, 24-hour auto-refresh):
//    Works ONLY for Dev Stores or stores owned by the same Shopify organisation.
//    Will fail on live/paid customer stores with "shop_not_permitted".
//
// The selected mode is stored as credentials.auth_mode so the UI can restore it.

type AuthMode = 'api_token' | 'client_credentials';

function ShopifyManualModal({
  existing,
  companyId,
  onSaved,
  onClose,
}: {
  existing:  Integration | null;
  companyId: string;
  onSaved:   () => void;
  onClose:   () => void;
}) {
  // Determine the stored mode, defaulting to api_token for new connections
  // (most users connecting real stores need the static token path).
  const storedMode: AuthMode =
    (existing?.credentials?.auth_mode as AuthMode | undefined) ??
    (existing?.credentials?.api_token ? 'api_token' : 'client_credentials');

  const [authMode,      setAuthMode]      = useState<AuthMode>(storedMode);
  const [storeDomain,   setStoreDomain]   = useState(existing?.credentials?.store_domain   ?? '');
  const [apiToken,      setApiToken]      = useState(existing?.credentials?.api_token       ?? '');
  const [clientId,      setClientId]      = useState(existing?.credentials?.client_id       ?? '');
  const [clientSecret,  setClientSecret]  = useState(existing?.credentials?.client_secret   ?? '');
  const [saving,        setSaving]        = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  const isConnected = !!existing?.is_active;

  // Token status display for client_credentials mode
  const tokenExpiresAt: string | null = existing?.credentials?.token_expires_at ?? null;
  const tokenValid = tokenExpiresAt ? new Date(tokenExpiresAt) > new Date() : false;

  const handleSave = async () => {
    const domain = storeDomain.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '').toLowerCase();

    if (!domain) { setError('יש להזין את כתובת החנות'); return; }
    if (!/^[a-z0-9][a-z0-9\-]*\.myshopify\.com$/.test(domain)) {
      setError('כתובת החנות חייבת להיות בפורמט: your-store.myshopify.com');
      return;
    }

    if (authMode === 'api_token') {
      const token = apiToken.trim();
      if (!token) { setError('יש להזין את ה-Admin API Access Token'); return; }
    } else {
      if (!clientId.trim())     { setError('יש להזין את ה-Client ID'); return; }
      if (!clientSecret.trim()) { setError('יש להזין את ה-Client Secret'); return; }
    }

    setSaving(true); setError(null);
    try {
      const prevCreds = existing?.credentials ?? {};

      // Determine which credentials changed so we can decide whether to clear
      // the cached exchange token.
      const domainChanged  = prevCreds.store_domain  !== domain;
      const credChanged    = authMode === 'api_token'
        ? prevCreds.api_token     !== apiToken.trim()
        : prevCreds.client_id     !== clientId.trim() || prevCreds.client_secret !== clientSecret.trim();
      const clearCached    = domainChanged || credChanged;

      const credentials: Record<string, string | null> = {
        store_domain: domain,
        auth_mode:    authMode,
        // Always persist both sets; unused fields are null so the backend
        // clearly knows which mode is active.
        api_token:    authMode === 'api_token' ? apiToken.trim() : null,
        client_id:    authMode === 'client_credentials' ? clientId.trim() : (prevCreds.client_id ?? null),
        client_secret: authMode === 'client_credentials' ? clientSecret.trim() : (prevCreds.client_secret ?? null),
        // Preserve or clear the cached exchange token
        access_token:     clearCached ? null : (prevCreds.access_token     ?? null),
        token_expires_at: clearCached ? null : (prevCreds.token_expires_at ?? null),
      };

      const { data: saved, error: e } = await supabase
        .from('company_integrations')
        .upsert(
          { company_id: companyId, provider_name: 'shopify_manual', credentials, is_active: true, updated_at: new Date().toISOString() },
          { onConflict: 'company_id,provider_name' },
        )
        .select('id')
        .maybeSingle();
      if (e) throw e;
      if (saved?.id) await verifyAfterSave(saved.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירת ההגדרות');
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!existing) return;
    if (!confirm('האם לנתק את האינטגרציה עם Shopify (חיבור ידני)?')) return;
    setDisconnecting(true); setError(null);
    try {
      const { error: e } = await supabase.from('company_integrations').delete().eq('id', existing.id);
      if (e) throw e;
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בניתוק');
    } finally { setDisconnecting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" dir="rtl">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-xl">🛍️</div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Shopify — חיבור ידני</h2>
              <p className="text-xs text-gray-400">Custom App — בחר את שיטת האימות המתאימה</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}

          {isConnected && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>מחובר לחנות <strong>{existing?.credentials?.store_domain}</strong></span>
              </div>
              {authMode === 'client_credentials' && (
                <p className={`text-xs px-1 ${
                  tokenExpiresAt
                    ? tokenValid ? 'text-green-600' : 'text-amber-600'
                    : 'text-gray-400'
                }`}>
                  {tokenExpiresAt
                    ? tokenValid
                      ? `✓ טוקן פעיל עד ${new Date(tokenExpiresAt).toLocaleString('he-IL', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}`
                      : `⚠ טוקן פג תוקף — יתרענן אוטומטית בקריאה הבאה`
                    : 'טוקן ייווצר אוטומטית בפעולה הראשונה'}
                </p>
              )}
              {authMode === 'api_token' && (
                <p className="text-xs px-1 text-green-600">✓ טוקן סטטי — פעיל ללא תפוגה</p>
              )}
            </div>
          )}

          {isConnected && existing && (
            <ConnectionIdentityCard companyId={companyId} integrationId={existing.id} providerName="shopify_manual" />
          )}

          {/* Auth mode selector */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">שיטת אימות</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setAuthMode('api_token'); setError(null); }}
                disabled={saving || disconnecting}
                className={`flex flex-col items-start px-4 py-3 rounded-xl border text-sm transition-colors
                  ${authMode === 'api_token'
                    ? 'border-green-500 bg-green-50 text-green-800'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <span className="font-semibold">Admin API Token</span>
                <span className="text-xs mt-0.5 opacity-70">לכל סוגי החנויות ✓</span>
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode('client_credentials'); setError(null); }}
                disabled={saving || disconnecting}
                className={`flex flex-col items-start px-4 py-3 rounded-xl border text-sm transition-colors
                  ${authMode === 'client_credentials'
                    ? 'border-green-500 bg-green-50 text-green-800'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <span className="font-semibold">Client ID + Secret</span>
                <span className="text-xs mt-0.5 opacity-70">חנויות פיתוח בלבד</span>
              </button>
            </div>
          </div>

          {/* Store Domain — always shown */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              כתובת החנות (Store Domain) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={storeDomain}
              onChange={e => setStoreDomain(e.target.value)}
              placeholder="your-store.myshopify.com"
              disabled={saving || disconnecting}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-green-400
                         disabled:bg-gray-50 disabled:text-gray-400"
              dir="ltr"
            />
            <p className="text-xs text-gray-400">הכנס רק את הדומיין ללא https://</p>
          </div>

          {/* ── Mode A: Admin API Token ── */}
          {authMode === 'api_token' && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Admin API Access Token <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={apiToken}
                  onChange={e => setApiToken(e.target.value)}
                  placeholder="shpat_xxxxxxxxxxxxxxxxxxxx"
                  disabled={saving || disconnecting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-green-400
                             disabled:bg-gray-50 disabled:text-gray-400"
                  dir="ltr"
                />
                <p className="text-xs text-gray-400">
                  Shopify Admin → Settings → Apps → Develop apps → [האפליקציה שלך] → API credentials → Admin API access token
                </p>
              </div>
              <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                <span className="shrink-0 mt-0.5">ℹ️</span>
                <span>
                  הטוקן מוצג פעם אחת בלבד בעת יצירת האפליקציה. אם פספסת אותו — מחק את האפליקציה וצור חדשה.
                  טוקן זה לא פג תוקף ועובד עם כל חנות Shopify, כולל חנויות פעילות.
                </span>
              </div>
            </>
          )}

          {/* ── Mode B: Client ID + Secret ── */}
          {authMode === 'client_credentials' && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={e => setClientId(e.target.value)}
                  placeholder="abc123def456..."
                  disabled={saving || disconnecting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-green-400
                             disabled:bg-gray-50 disabled:text-gray-400"
                  dir="ltr"
                />
                <p className="text-xs text-gray-400">
                  Shopify Admin → Apps → Develop apps → [האפליקציה שלך] → API credentials → API key
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={clientSecret}
                  onChange={e => setClientSecret(e.target.value)}
                  placeholder="shpss_..."
                  disabled={saving || disconnecting}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-green-400
                             disabled:bg-gray-50 disabled:text-gray-400"
                  dir="ltr"
                />
                <p className="text-xs text-gray-400">
                  Shopify Admin → Apps → Develop apps → [האפליקציה שלך] → API credentials → API secret key
                </p>
              </div>
              <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                <span className="shrink-0 mt-0.5">⚠️</span>
                <span>
                  שיטה זו עובדת <strong>רק עם חנויות פיתוח (Dev Stores)</strong> או חנויות שבבעלות אותה ארגון Shopify.
                  לחנויות לקוחות אמיתיות — השתמש ב-"Admin API Token".
                </span>
              </div>
              <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                <span className="shrink-0 mt-0.5">🔄</span>
                <span>
                  הטוקן מתרענן אוטומטית כל 24 שעות דרך השרת שלנו — אין צורך בעדכון ידני.
                </span>
              </div>
            </>
          )}

          <a
            href="https://shopify.dev/docs/apps/build/custom-apps"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />מדריך יצירת Custom App ב-Shopify
          </a>

          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
            <span className="shrink-0 mt-0.5">🔒</span>
            הטוקן מאוחסן בצפנה בשרת. גישה לנתונים מוגבלת לחברי החברה בלבד.
          </div>

          {existing?.last_sync_at && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />סנכרון אחרון: {fmtDateTime(existing.last_sync_at)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div>
            {isConnected && (
              <button
                onClick={handleDisconnect}
                disabled={disconnecting || saving}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Unplug className="w-4 h-4" />
                {disconnecting ? 'מנתק…' : 'נתק חיבור'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving || disconnecting}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              onClick={handleSave}
              disabled={saving || disconnecting}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700
                         text-white text-sm font-semibold rounded-lg transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin" />שומר…</>
                : <><Plug className="w-4 h-4" />{isConnected ? 'עדכן הגדרות' : 'חבר עכשיו'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ShopifyOpsModal ──────────────────────────────────────────────────────────
// "ניהול ממשק" modal for connected Shopify integrations.
// Lets the user fetch products from Shopify, select them, and import
// them into inventory_products.

type FetchState = 'idle' | 'loading' | 'done' | 'error';
type ImportState = 'idle' | 'importing' | 'done' | 'error';

function ShopifyOpsModal({
  companyId,
  integration,
  onClose,
  onImportDone,
}: {
  companyId:    string;
  integration:  Integration;
  onClose:      () => void;
  onImportDone: (count: number) => void;
}) {
  // Fetch state
  const [fetchState,    setFetchState]    = useState<FetchState>('idle');
  const [fetchError,    setFetchError]    = useState<string | null>(null);
  const [products,      setProducts]      = useState<ShopifyProduct[]>([]);
  const [filtered,      setFiltered]      = useState<ShopifyProduct[]>([]);
  const [searchQuery,   setSearchQuery]   = useState('');

  // Selection
  const [selected,      setSelected]      = useState<Set<number>>(new Set());

  // Import state
  const [importState,   setImportState]   = useState<ImportState>('idle');
  const [importError,   setImportError]   = useState<string | null>(null);
  const [importResults, setImportResults] = useState<{ imported: number; skipped: number } | null>(null);

  // Accordion section open
  const [sectionOpen,   setSectionOpen]   = useState(true);

  const searchRef = useRef<HTMLInputElement>(null);

  // Derive the Supabase URL from the environment (same as the rest of the app)
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  const edgeFnUrl   = `${supabaseUrl}/functions/v1/shopify-fetch-products`;

  // ── Filter products by search query ───────────────────────────────────────
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFiltered(products);
    } else {
      const q = searchQuery.toLowerCase();
      setFiltered(products.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.vendor.toLowerCase().includes(q)
      ));
    }
  }, [searchQuery, products]);

  // ── Fetch products from Edge Function ─────────────────────────────────────
  const handleFetch = async () => {
    setFetchState('loading');
    setFetchError(null);
    setProducts([]);
    setFiltered([]);
    setSelected(new Set());
    setImportResults(null);
    setImportState('idle');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('לא מחובר – נא התחבר מחדש');

      const res = await fetch(edgeFnUrl, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ company_id: companyId }),
      });

      const json = await res.json() as { products?: ShopifyProduct[]; error?: string; total?: number };

      if (!res.ok) {
        throw new Error(json.error ?? `שגיאת שרת (HTTP ${res.status})`);
      }

      const fetched = json.products ?? [];
      setProducts(fetched);
      setFiltered(fetched);
      // Pre-select all by default
      setSelected(new Set(fetched.map(p => p.shopify_id)));
      setFetchState('done');
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : 'שגיאה לא ידועה');
      setFetchState('error');
    }
  };

  // ── Select / deselect helpers ─────────────────────────────────────────────
  const toggleOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selected.has(p.shopify_id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.delete(p.shopify_id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.add(p.shopify_id));
        return next;
      });
    }
  };

  // ── Import selected products ───────────────────────────────────────────────
  //
  // Phase 53 fix: call the import_selected_products() SECURITY DEFINER RPC
  // (created in Phase 49) instead of doing a raw client-side upsert.
  //
  // Why the old approach failed:
  //  1. It upserted directly into inventory_products via the Supabase JS client.
  //     The table has a (company_id, sku) UNIQUE constraint; Shopify products
  //     with the same or empty SKU caused constraint violations, silently
  //     rejecting entire batches → "0 imported, N skipped".
  //  2. It conflicted on (company_id, shopify_id) — the Phase 29 column — but
  //     Phase 49 replaced the deduplication key with external_source_id
  //     (format: 'shopify:{id}'). The old conflict target no longer matched.
  //  3. The client-side supabase.from().upsert() is subject to RLS and returned
  //     a 401 because the anon client wasn't sending the session JWT in the
  //     upsert request correctly when called from inside the modal.
  //
  // The RPC handles all of this server-side:
  //  • SECURITY DEFINER → bypasses RLS for the INSERT while validating
  //    membership inside the function body.
  //  • Deduplicates on (company_id, external_source_id) = 'shopify:{shopify_id}'.
  //  • Per-row exception handling → one bad product never aborts the batch.
  //  • Returns exact imported_count / skipped_count from the DB.

  const handleImport = async () => {
    const toImport = products.filter(p => selected.has(p.shopify_id));
    if (toImport.length === 0) return;

    setImportState('importing');
    setImportError(null);
    setImportResults(null);

    try {
      // Build the JSONB payload expected by import_selected_products():
      //   external_id  — Shopify numeric product ID (used to build external_source_id)
      //   name         — product title (required; RPC skips rows with empty name)
      //   sku          — raw Shopify SKU; RPC will slug-ify it if empty
      //   price        — stored excl. VAT; user adjusts later via Products page
      //   cost         — nullable
      //   image_url    — nullable
      const payload = toImport.map(p => ({
        external_id: String(p.shopify_id),
        name:        p.title,
        sku:         p.sku || '',           // RPC handles empty → slug fallback
        price:       p.price   ?? 0,
        cost:        p.cost    ?? null,
        image_url:   p.image_url ?? null,
      }));

      // Call the SECURITY DEFINER RPC — handles auth, dedup, and per-row errors
      const { data: result, error: rpcErr } = await supabase
        .rpc('import_selected_products', {
          p_company_id: companyId,
          p_source:     'shopify',
          p_products:   payload,
        });

      if (rpcErr) throw rpcErr;

      const imported = (result as any)?.imported_count ?? 0;
      const skipped  = (result as any)?.skipped_count  ?? 0;

      // Update last_sync_at on the integration row (best-effort, non-fatal)
      supabase
        .from('company_integrations')
        .update({ last_sync_at: new Date().toISOString() })
        .eq('id', integration.id)
        .then(({ error: syncErr }) => {
          if (syncErr) console.warn('handleImport: last_sync_at update failed', syncErr.message);
        });

      setImportResults({ imported, skipped });
      setImportState('done');
      onImportDone(imported);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'שגיאה בייבוא');
      setImportState('error');
    }
  };

  const selectedCount = selected.size;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 pt-20 pb-6 overflow-y-auto" dir="rtl">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: 'calc(100vh - 8rem)' }}>

        {/* ── Modal Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-xl">🛍️</div>
            <div>
              <h2 className="text-base font-bold text-gray-900">ניהול ממשק Shopify</h2>
              <p className="text-xs text-gray-400">
                חנות: <span className="font-mono text-gray-600">{integration.credentials.store_domain ?? integration.credentials.store_url}</span>
                {integration.last_sync_at && (
                  <> &middot; סנכרון אחרון: {fmtDateTime(integration.last_sync_at)}</>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Product Sync Section ── */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Accordion header */}
            <button
              onClick={() => setSectionOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-right"
            >
              <div className="flex items-center gap-2.5">
                <Package className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-800">סנכרון מוצרים</span>
                {products.length > 0 && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    {products.length} מוצרים
                  </span>
                )}
              </div>
              {sectionOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {sectionOpen && (
              <div className="p-5 space-y-4">

                {/* Fetch button + description */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    onClick={handleFetch}
                    disabled={fetchState === 'loading' || importState === 'importing'}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700
                               text-white text-sm font-semibold rounded-xl transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {fetchState === 'loading'
                      ? <><Loader2 className="w-4 h-4 animate-spin" />טוען מוצרים…</>
                      : <><RefreshCw className="w-4 h-4" />טען מוצרים משופיפיי</>}
                  </button>
                  <p className="text-xs text-gray-400">
                    שולף עד 2,500 מוצרים פעילים מחנות Shopify שלך.
                    הפעולה בטוחה ואינה משנה נתונים בשופיפיי.
                  </p>
                </div>

                {/* Fetch error */}
                {fetchState === 'error' && fetchError && (
                  <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <strong>שגיאה בטעינת מוצרים:</strong> {fetchError}
                    </div>
                  </div>
                )}

                {/* Import success banner */}
                {importState === 'done' && importResults && (
                  <div className="flex items-start gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      {importResults.imported > 0
                        ? <>יובאו / עודכנו <strong>{importResults.imported}</strong> מוצרים בהצלחה לקטלוג המלאי.</>
                        : <>לא יובאו מוצרים חדשים — ייתכן שכולם כבר קיימים במערכת.</>
                      }
                      {importResults.skipped > 0 && (
                        <> <span className="opacity-75">({importResults.skipped} דולגו עקב שם חסר או שגיאה)</span></>
                      )}
                    </div>
                  </div>
                )}

                {/* Import error */}
                {importState === 'error' && importError && (
                  <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div><strong>שגיאה בייבוא:</strong> {importError}</div>
                  </div>
                )}

                {/* Product table */}
                {fetchState === 'done' && products.length > 0 && (
                  <div className="space-y-3">
                    {/* Search + selection summary */}
                    <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          ref={searchRef}
                          type="text"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          placeholder="חפש לפי שם, מק״ט, ספק…"
                          className="w-full pr-9 pl-3 py-2 border border-gray-300 rounded-lg text-sm
                                     focus:outline-none focus:ring-2 focus:ring-green-400"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {selectedCount} נבחרו מתוך {filtered.length} מוצרים
                        </span>
                        <button
                          onClick={handleImport}
                          disabled={selectedCount === 0 || importState === 'importing'}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700
                                     text-white text-sm font-semibold rounded-xl transition-colors
                                     disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                        >
                          {importState === 'importing'
                            ? <><Loader2 className="w-4 h-4 animate-spin" />מייבא…</>
                            : <><Download className="w-4 h-4" />ייבא מוצרים נבחרים ({selectedCount})</>}
                        </button>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                            <tr>
                              <th className="px-3 py-3 w-10">
                                <input
                                  type="checkbox"
                                  checked={allFilteredSelected}
                                  onChange={toggleAll}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              </th>
                              <th className="px-3 py-3 w-14 text-right text-xs font-semibold text-gray-500">תמונה</th>
                              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500">שם מוצר</th>
                              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">מק״ט</th>
                              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">ספק</th>
                              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">מחיר</th>
                              <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">וריאציות</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filtered.map(p => {
                              const isSelected = selected.has(p.shopify_id);
                              return (
                                <tr
                                  key={p.shopify_id}
                                  onClick={() => toggleOne(p.shopify_id)}
                                  className={`cursor-pointer transition-colors ${
                                    isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  {/* Checkbox */}
                                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleOne(p.shopify_id)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                  </td>

                                  {/* Image */}
                                  <td className="px-3 py-2.5">
                                    {p.image_url ? (
                                      <img
                                        src={p.image_url}
                                        alt={p.title}
                                        className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <Package className="w-4 h-4 text-gray-400" />
                                      </div>
                                    )}
                                  </td>

                                  {/* Title */}
                                  <td className="px-3 py-2.5">
                                    <p className="font-medium text-gray-800 line-clamp-2 max-w-xs">{p.title}</p>
                                    {p.product_type && (
                                      <p className="text-xs text-gray-400 mt-0.5">{p.product_type}</p>
                                    )}
                                  </td>

                                  {/* SKU */}
                                  <td className="px-3 py-2.5">
                                    <span className="font-mono text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                      {p.sku || '—'}
                                    </span>
                                  </td>

                                  {/* Vendor */}
                                  <td className="px-3 py-2.5 text-xs text-gray-500">{p.vendor || '—'}</td>

                                  {/* Price */}
                                  <td className="px-3 py-2.5 text-sm font-semibold text-gray-700 whitespace-nowrap">
                                    {fmtPrice(p.price)}
                                  </td>

                                  {/* Variants count */}
                                  <td className="px-3 py-2.5">
                                    {p.variants.length > 1 ? (
                                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">
                                        {p.variants.length} וריאציות
                                      </span>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* No search results */}
                      {filtered.length === 0 && searchQuery && (
                        <div className="py-8 text-center text-sm text-gray-400">
                          לא נמצאו מוצרים תואמים לחיפוש &ldquo;{searchQuery}&rdquo;
                        </div>
                      )}
                    </div>

                    {/* Bottom import bar */}
                    <div className="flex items-center justify-between pt-1">
                      <p className="text-xs text-gray-400">
                        💡 המוצרים יובאו לקטלוג המלאי שלך. מוצרים קיימים יזוהו לפי Shopify ID ויעודכנו אוטומטית ללא כפילויות.
                      </p>
                      <button
                        onClick={handleImport}
                        disabled={selectedCount === 0 || importState === 'importing'}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                                   text-white text-sm font-semibold rounded-xl transition-colors
                                   disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                      >
                        {importState === 'importing'
                          ? <><Loader2 className="w-4 h-4 animate-spin" />מייבא…</>
                          : <><Download className="w-4 h-4" />ייבא {selectedCount} מוצרים נבחרים</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Empty state – fetched but no products */}
                {fetchState === 'done' && products.length === 0 && (
                  <div className="py-10 text-center">
                    <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">לא נמצאו מוצרים פעילים בחנות Shopify.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Future sections placeholder ── */}
          <div className="border border-dashed border-gray-200 rounded-xl p-5 opacity-50">
            <div className="flex items-center gap-2.5 text-gray-400">
              <LayoutGrid className="w-5 h-5" />
              <span className="font-semibold text-sm">סנכרון הזמנות — בקרוב</span>
            </div>
          </div>
          <div className="border border-dashed border-gray-200 rounded-xl p-5 opacity-50">
            <div className="flex items-center gap-2.5 text-gray-400">
              <Wrench className="w-5 h-5" />
              <span className="font-semibold text-sm">עדכון מלאי בזמן אמת (Webhook) — בקרוב</span>
            </div>
          </div>

        </div>

        {/* ── Modal Footer ── */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-400">
            🔒 הפעולות מתבצעות דרך שרת מאובטח. הטוקן שלך לעולם אינו נשלח לדפדפן.
          </p>
          <button onClick={onClose}
                  className="px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ProviderCard ─────────────────────────────────────────────────────────────

// Map provider id → settings route segment
const SETTINGS_ROUTE: Record<string, string> = {
  shopify_manual: 'shopify',
  shopify:        'shopify',
  rivhit:         'rivhit',
  tranzila:       'tranzila',
};

function ProviderCard({
  provider,
  integration,
  onConfigure,
  onManage,
  onSettings,
}: {
  provider:    ProviderDef;
  integration: Integration | null;
  onConfigure: (p: ProviderDef) => void;
  onManage:    (p: ProviderDef) => void;
  onSettings:  (p: ProviderDef) => void;
}) {
  const isComingSoon = provider.status === 'coming_soon';
  const isConnected  = !!integration?.is_active;

  // Which providers have an "Operations" modal (Phase 29+)
  const hasOpsModal     = provider.id === 'shopify' || provider.id === 'shopify_manual';
  // Which providers have a dedicated settings page
  const hasSettingsPage = isConnected && !!SETTINGS_ROUTE[provider.id];

  return (
    <div className={`relative flex flex-col gap-4 p-5 rounded-2xl border-2 transition-all duration-150
                    ${isComingSoon
                      ? 'border-gray-100 bg-gray-50 opacity-60'
                      : isConnected
                        ? 'border-green-200 bg-green-50/30 hover:shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-200 hover:shadow-md'}`}>

      {/* Coming-soon badge */}
      {isComingSoon && (
        <span className="absolute top-3 left-3 px-2 py-0.5 bg-gray-200 text-gray-500 text-xs font-semibold rounded-full">
          בקרוב
        </span>
      )}

      {/* Connected badge */}
      {isConnected && (
        <span className="absolute top-3 left-3 flex items-center gap-1
                          px-2 py-0.5 bg-green-100 text-green-700
                          text-xs font-semibold rounded-full border border-green-200">
          <CheckCircle2 className="w-3 h-3" />מחובר
        </span>
      )}

      {/* Logo + name */}
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-sm ${provider.logoBg}`}>
          {provider.logoText}
        </div>
        <div>
          <p className="font-bold text-gray-800">{provider.label}</p>
          <p className="text-xs text-gray-400 mt-0.5 leading-snug">{provider.description}</p>
        </div>
      </div>

      {/* Last sync info */}
      {integration?.last_sync_at && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />סנכרון: {fmtDateTime(integration.last_sync_at)}
        </p>
      )}

      {!isComingSoon && isConnected && integration && (
        <ConnectionIdentityCard
          key={`${integration.id}:${integration.updated_at}`}
          companyId={integration.company_id}
          integrationId={integration.id}
          providerName={integration.provider_name}
          variant="compact"
          autoCheck
        />
      )}

      {/* Action buttons */}
      {!isComingSoon && isConnected && (
        <div className="mt-auto flex flex-col gap-2">
          {/* Primary: Manage (Operations modal) */}
          {hasOpsModal && (
            <button
              onClick={() => onManage(provider)}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl
                         text-sm font-semibold transition-colors
                         bg-green-600 text-white hover:bg-green-700 shadow-sm"
            >
              <Settings className="w-4 h-4" /> ניהול ממשק
            </button>
          )}
          {/* Settings page */}
          {hasSettingsPage && (
            <button
              onClick={() => onSettings(provider)}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl
                         text-sm font-semibold transition-colors
                         bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
            >
              <SlidersHorizontal className="w-4 h-4" /> הגדרות ממשק
            </button>
          )}
          {/* Secondary: Edit credentials */}
          <button
            onClick={() => onConfigure(provider)}
            className="flex items-center justify-center gap-2 py-2 rounded-xl
                       text-sm font-medium transition-colors
                       bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            <Plug className="w-3.5 h-3.5" /> עדכן הגדרות חיבור
          </button>
        </div>
      )}

      {!isComingSoon && !isConnected && (
        <button
          onClick={() => onConfigure(provider)}
          className="mt-auto flex items-center justify-center gap-2 py-2.5 rounded-xl
                     text-sm font-semibold transition-colors
                     bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
        >
          <Plug className="w-4 h-4" /> חבר עכשיו
        </button>
      )}

      {isComingSoon && (
        <div className="mt-auto flex items-center justify-center gap-1.5 py-2.5 text-sm text-gray-400 font-medium">
          <Clock className="w-4 h-4" />זמין בקרוב
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function IntegrationsHubPage() {
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [toast,        setToast]        = useState<{ ok: boolean; msg: string } | null>(null);

  // Config modal (edit credentials — non-Shopify providers)
  const [configProvider,      setConfigProvider]      = useState<ProviderDef | null>(null);
  // Shopify OAuth modal
  const [shopifyOAuthOpen,    setShopifyOAuthOpen]    = useState(false);
  // Shopify Manual modal (Phase 53)
  const [shopifyManualOpen,   setShopifyManualOpen]   = useState(false);
  // Ops modal (Shopify operations – manage)
  const [opsProvider,         setOpsProvider]         = useState<ProviderDef | null>(null);

  // ── Fetch saved integrations ───────────────────────────────────────────────
  const fetchIntegrations = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from('company_integrations')
        .select('*')
        .eq('company_id', activeCompanyId)
        .order('created_at');
      if (e) throw e;
      setIntegrations((data ?? []) as Integration[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת אינטגרציות');
    } finally { setLoading(false); }
  }, [activeCompanyId]);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = (msg: string, ok = true) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 5000);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSaved = useCallback(() => {
    setConfigProvider(null);
    fetchIntegrations();
    showToast('ההגדרות נשמרו בהצלחה ✓');
  }, [fetchIntegrations]);

  // Called after Shopify OAuth popup closes (success or popup-closed poll)
  const handleShopifyConnected = useCallback(() => {
    setShopifyOAuthOpen(false);
    fetchIntegrations();
    showToast('חנות Shopify חוברה בהצלחה ✓');
  }, [fetchIntegrations]);

  // Called after Shopify Manual credentials are saved (Phase 53)
  const handleShopifyManualSaved = useCallback(() => {
    setShopifyManualOpen(false);
    fetchIntegrations();
    showToast('חנות Shopify חוברה בהצלחה (חיבור ידני) ✓');
  }, [fetchIntegrations]);

  const handleImportDone = useCallback((count: number) => {
    fetchIntegrations(); // refresh last_sync_at badge
    showToast(`יובאו ${count} מוצרים בהצלחה לקטלוג המלאי ✓`);
  }, [fetchIntegrations]);

  const handleOpenSettings = useCallback((p: ProviderDef) => {
    const segment = SETTINGS_ROUTE[p.id] ?? p.id;
    navigate(`/app/settings/integrations/${segment}`);
  }, [navigate]);

  // ── Integration map ────────────────────────────────────────────────────────
  const integrationMap = Object.fromEntries(integrations.map(i => [i.provider_name, i]));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">

      <SettingsTabs />

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ממשקים וחיבורים</h1>
          <p className="text-sm text-gray-500 mt-1">חבר את Axelio לפלטפורמות eCommerce, ERP ו-CRM שלך.</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-xl text-xs font-medium text-blue-700">
          {integrations.filter(i => i.is_active).length} חיבורים פעילים
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border
                          ${toast.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {toast.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {toast.msg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען אינטגרציות…</span>
        </div>
      )}

      {/* Provider grid */}
      {!loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROVIDERS.map(provider => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              integration={integrationMap[provider.id] ?? null}
              onConfigure={p => {
                if (p.id === 'shopify') {
                  setShopifyOAuthOpen(true);
                } else if (p.id === 'shopify_manual') {
                  setShopifyManualOpen(true);
                } else {
                  setConfigProvider(p);
                }
              }}
              onManage={setOpsProvider}
              onSettings={handleOpenSettings}
            />
          ))}
        </div>
      )}

      {/* Security footer */}
      {!loading && (
        <div className="flex items-start gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-500">
          <span className="shrink-0 text-base mt-0.5">🔒</span>
          <p>
            <strong>אבטחה:</strong> כל פרטי הגישה מאוחסנים בצפנה בתוך מסד הנתונים של Supabase.
            גישה לנתונים מוגבלת לחברי החברה הרשומים בלבד על-ידי מדיניות RLS.
            קריאות ה-API לשירותים חיצוניים מתבצעות דרך שרת מאובטח (Edge Function) ולא ישירות מהדפדפן.
            לעולם אין לשתף API tokens עם גורמים חיצוניים.
          </p>
        </div>
      )}

      {/* Shopify OAuth Modal (Phase 48) */}
      {shopifyOAuthOpen && (
        <ShopifyOAuthModal
          existing={integrationMap['shopify'] ?? null}
          onConnected={handleShopifyConnected}
          onClose={() => setShopifyOAuthOpen(false)}
        />
      )}

      {/* Shopify Manual Modal (Phase 53) */}
      {shopifyManualOpen && activeCompanyId && (
        <ShopifyManualModal
          existing={integrationMap['shopify_manual'] ?? null}
          companyId={activeCompanyId}
          onSaved={handleShopifyManualSaved}
          onClose={() => setShopifyManualOpen(false)}
        />
      )}

      {/* Config Modal — non-Shopify providers */}
      {configProvider && activeCompanyId && (
        <ConfigModal
          provider={configProvider}
          existing={integrationMap[configProvider.id] ?? null}
          companyId={activeCompanyId}
          onSaved={handleSaved}
          onClose={() => setConfigProvider(null)}
        />
      )}

      {/* Shopify Ops Modal */}
      {opsProvider?.id === 'shopify' && activeCompanyId && integrationMap['shopify'] && (
        <ShopifyOpsModal
          companyId={activeCompanyId}
          integration={integrationMap['shopify']}
          onClose={() => setOpsProvider(null)}
          onImportDone={handleImportDone}
        />
      )}

      {/* Shopify Manual Ops Modal — uses the same ShopifyOpsModal but with the manual integration */}
      {opsProvider?.id === 'shopify_manual' && activeCompanyId && integrationMap['shopify_manual'] && (
        <ShopifyOpsModal
          companyId={activeCompanyId}
          integration={integrationMap['shopify_manual']}
          onClose={() => setOpsProvider(null)}
          onImportDone={handleImportDone}
        />
      )}
    </div>
  );
}
